// Permission utility functions for role-based access control

// Data access scopes
export const DATA_SCOPE = {
  ALL: 'all',                    // See all data (managers, admins)
  OWN: 'own',                    // See only assigned to me
  TEAM: 'team',                  // See my team's data
  MARKET: 'market',              // See my market's data
  TEAM_AND_MARKET: 'team_market' // See intersection of team and market
};

// Modules in the system
export const MODULES = {
  DASHBOARD: 'dashboard',
  BOARD: 'board',
  SALES_HUB: 'sales_hub',
  LEADS: 'leads',
  JOBS: 'jobs',
  USERS: 'users',
  TEAMS: 'teams',
  MARKETS: 'markets',
  ROLES: 'roles',
  SETTINGS: 'settings',
  REPORTS: 'reports'
};

// Feature-specific permissions (for marketing hub features)
export const FEATURE_PERMISSIONS = {
  MARKETING_TRACKER: 'marketing_tracker',
  ON_TRACK_PACING: 'on_track_pacing',
  ADS_MANAGER: 'ads_manager',
  TARGET_NUMBERS: 'target_numbers',
  MARKETING_SETTINGS: 'marketing_settings',
  // Phase 3.5: Content Studio permissions
  CONTENT_STUDIO_CREATE: 'content_studio_create',
  CONTENT_STUDIO_EDIT: 'content_studio_edit',
  CONTENT_STUDIO_DELETE: 'content_studio_delete',
  CONTENT_STUDIO_TEMPLATES: 'content_studio_templates',
  // Phase 3.5: Scheduler permissions
  SCHEDULER_CREATE: 'scheduler_create',
  SCHEDULER_EDIT: 'scheduler_edit',
  SCHEDULER_DELETE: 'scheduler_delete',
  SCHEDULER_STATUS: 'scheduler_status',
  // Phase 7: Approval Workflow permissions
  APPROVE_CONTENT: 'approve_content',
  APPROVE_SCHEDULE: 'approve_schedule',
  VIEW_APPROVALS: 'view_approvals'
};

// Default permissions for manager roles
const MANAGER_ROLES = [
  'super_admin',
  'admin',
  'ceo',
  'president',
  'president_&_ceo',
  'leadership',
  'director_of_sales',
  'sales_manager',
  'operations_manager',
  'general_manager',
  'business_operations_manager',
  'production_manager',
  'call_center_manager'
];

// Get data access scope for a role
export const getDataScope = (role, rolePermissions = null) => {
  // Check custom permissions first
  if (rolePermissions?.data_access?.scope) {
    return rolePermissions.data_access.scope;
  }
  
  // Default scopes based on role
  if (MANAGER_ROLES.includes(role)) {
    return DATA_SCOPE.ALL;
  }
  
  // Default for non-managers
  return DATA_SCOPE.OWN;
};

// Check if user can see all jobs (backward compatibility)
export const canSeeAllJobs = (role, rolePermissions = null) => {
  const scope = getDataScope(role, rolePermissions);
  return scope === DATA_SCOPE.ALL;
};

// Filter jobs based on user's data access scope
export const filterJobsByPermissions = (jobs, user, users = [], rolePermissions = null) => {
  if (!jobs || jobs.length === 0) return [];
  if (!user) return [];

  const scope = getDataScope(user.role, rolePermissions);
  const userId = user.id;

  switch (scope) {
    case DATA_SCOPE.ALL:
      // See everything
      return jobs;

    case DATA_SCOPE.OWN:
      // See only jobs assigned to me
      return jobs.filter(job => job.assigned_to === userId);

    case DATA_SCOPE.TEAM:
      // See jobs assigned to anyone on my team(s)
      if (!user.teams || user.teams.length === 0) {
        return jobs.filter(job => job.assigned_to === userId);
      }
      
      // Get all users in my teams
      const teamMembers = users.filter(u => 
        u.teams && u.teams.some(teamId => user.teams.includes(teamId))
      );
      const teamMemberIds = teamMembers.map(u => u.id);
      
      return jobs.filter(job => 
        job.assigned_to && teamMemberIds.includes(job.assigned_to)
      );

    case DATA_SCOPE.MARKET:
      // See jobs in my market(s)
      if (!user.markets || user.markets.length === 0) {
        return jobs.filter(job => job.assigned_to === userId);
      }
      
      return jobs.filter(job => 
        job.market_id && user.markets.includes(job.market_id)
      );

    case DATA_SCOPE.TEAM_AND_MARKET:
      // See jobs that are in my teams AND my markets
      if ((!user.teams || user.teams.length === 0) && 
          (!user.markets || user.markets.length === 0)) {
        return jobs.filter(job => job.assigned_to === userId);
      }
      
      // Get team members
      const myTeamMembers = users.filter(u => 
        u.teams && u.teams.some(teamId => user.teams?.includes(teamId))
      );
      const myTeamMemberIds = myTeamMembers.map(u => u.id);
      
      return jobs.filter(job => {
        const inMyTeam = job.assigned_to && myTeamMemberIds.includes(job.assigned_to);
        const inMyMarket = job.market_id && user.markets?.includes(job.market_id);
        return inMyTeam || inMyMarket;
      });

    default:
      return jobs.filter(job => job.assigned_to === userId);
  }
};

// Check module permissions
export const hasModulePermission = (module, action, rolePermissions = null) => {
  if (!rolePermissions || !rolePermissions[module]) {
    // Default: allow all for backward compatibility
    return true;
  }
  
  const modulePerms = rolePermissions[module];
  return modulePerms[action] === true;
};

// Get readable scope name
export const getScopeName = (scope) => {
  const names = {
    [DATA_SCOPE.ALL]: 'All Data',
    [DATA_SCOPE.OWN]: 'Own Assignments Only',
    [DATA_SCOPE.TEAM]: 'Team Data',
    [DATA_SCOPE.MARKET]: 'Market Data',
    [DATA_SCOPE.TEAM_AND_MARKET]: 'Team & Market Data'
  };
  return names[scope] || 'Unknown';
};

// Default permission structure for a new role
export const getDefaultPermissions = () => ({
  data_access: {
    scope: DATA_SCOPE.OWN
  },
  [MODULES.DASHBOARD]: { read: true, write: false, delete: false },
  [MODULES.BOARD]: { read: true, write: true, delete: false },
  [MODULES.SALES_HUB]: { read: true, write: false, delete: false },
  [MODULES.LEADS]: { read: true, write: true, delete: false },
  [MODULES.JOBS]: { read: true, write: true, delete: false },
  [MODULES.USERS]: { read: false, write: false, delete: false },
  [MODULES.TEAMS]: { read: true, write: false, delete: false },
  [MODULES.MARKETS]: { read: true, write: false, delete: false },
  [MODULES.ROLES]: { read: false, write: false, delete: false },
  [MODULES.SETTINGS]: { read: false, write: false, delete: false },
  [MODULES.REPORTS]: { read: true, write: false, delete: false }
});

// Manager default permissions
export const getManagerPermissions = () => ({
  data_access: {
    scope: DATA_SCOPE.ALL
  },
  [MODULES.DASHBOARD]: { read: true, write: true, delete: false },
  [MODULES.BOARD]: { read: true, write: true, delete: true },
  [MODULES.SALES_HUB]: { read: true, write: true, delete: false },
  [MODULES.LEADS]: { read: true, write: true, delete: true },
  [MODULES.JOBS]: { read: true, write: true, delete: true },
  [MODULES.USERS]: { read: true, write: true, delete: false },
  [MODULES.TEAMS]: { read: true, write: true, delete: true },
  [MODULES.MARKETS]: { read: true, write: true, delete: true },
  [MODULES.ROLES]: { read: true, write: false, delete: false },
  [MODULES.SETTINGS]: { read: true, write: true, delete: false },
  [MODULES.REPORTS]: { read: true, write: true, delete: false }
});

// Check if user has feature permission
export const hasFeaturePermission = (user, featureKey) => {
  if (!user) return false;
  
  // Super admin and admin always have all permissions
  const role = user.role || '';
  if (['super_admin', 'admin'].includes(role)) return true;
  
  // CEO also has all permissions by default
  if (role === 'ceo') return true;
  
  // Check user's specific feature permissions
  const featurePermissions = user.feature_permissions || {};
  return featurePermissions[featureKey] === true;
};

// Get default feature permissions for a role
export const getDefaultFeaturePermissions = (role) => {
  // Admins and CEO get all permissions by default
  if (['super_admin', 'admin', 'ceo'].includes(role)) {
    return {
      [FEATURE_PERMISSIONS.MARKETING_TRACKER]: true,
      [FEATURE_PERMISSIONS.ON_TRACK_PACING]: true,
      [FEATURE_PERMISSIONS.ADS_MANAGER]: true,
      [FEATURE_PERMISSIONS.TARGET_NUMBERS]: true,
      [FEATURE_PERMISSIONS.MARKETING_SETTINGS]: true,
      // Content Studio - full access
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES]: true,
      // Scheduler - full access
      [FEATURE_PERMISSIONS.SCHEDULER_CREATE]: true,
      [FEATURE_PERMISSIONS.SCHEDULER_EDIT]: true,
      [FEATURE_PERMISSIONS.SCHEDULER_DELETE]: true,
      [FEATURE_PERMISSIONS.SCHEDULER_STATUS]: true
    };
  }
  
  // Marketing managers get marketing permissions (create + schedule, no delete templates)
  if (['marketing_manager', 'director_of_marketing'].includes(role)) {
    return {
      [FEATURE_PERMISSIONS.MARKETING_TRACKER]: true,
      [FEATURE_PERMISSIONS.ON_TRACK_PACING]: true,
      [FEATURE_PERMISSIONS.ADS_MANAGER]: true,
      [FEATURE_PERMISSIONS.TARGET_NUMBERS]: true,
      [FEATURE_PERMISSIONS.MARKETING_SETTINGS]: true,
      // Content Studio - create + edit, limited template access
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE]: true,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES]: false, // Cannot edit templates
      // Scheduler - create + edit + status, no delete
      [FEATURE_PERMISSIONS.SCHEDULER_CREATE]: true,
      [FEATURE_PERMISSIONS.SCHEDULER_EDIT]: true,
      [FEATURE_PERMISSIONS.SCHEDULER_DELETE]: false,
      [FEATURE_PERMISSIONS.SCHEDULER_STATUS]: true
    };
  }
  
  // President and Director of Sales - view + limited create
  if (['president', 'director_of_sales'].includes(role)) {
    return {
      [FEATURE_PERMISSIONS.MARKETING_TRACKER]: true,
      [FEATURE_PERMISSIONS.ON_TRACK_PACING]: true,
      [FEATURE_PERMISSIONS.ADS_MANAGER]: false,
      [FEATURE_PERMISSIONS.TARGET_NUMBERS]: true,
      [FEATURE_PERMISSIONS.MARKETING_SETTINGS]: false,
      // Content Studio - view only
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE]: false,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT]: false,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE]: false,
      [FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES]: false,
      // Scheduler - view only
      [FEATURE_PERMISSIONS.SCHEDULER_CREATE]: false,
      [FEATURE_PERMISSIONS.SCHEDULER_EDIT]: false,
      [FEATURE_PERMISSIONS.SCHEDULER_DELETE]: false,
      [FEATURE_PERMISSIONS.SCHEDULER_STATUS]: false
    };
  }
  
  // Default: view-only access to marketing features
  return {
    [FEATURE_PERMISSIONS.MARKETING_TRACKER]: true,
    [FEATURE_PERMISSIONS.ON_TRACK_PACING]: true,
    [FEATURE_PERMISSIONS.ADS_MANAGER]: false,
    [FEATURE_PERMISSIONS.TARGET_NUMBERS]: false,
    [FEATURE_PERMISSIONS.MARKETING_SETTINGS]: false,
    // Content Studio - view only
    [FEATURE_PERMISSIONS.CONTENT_STUDIO_CREATE]: false,
    [FEATURE_PERMISSIONS.CONTENT_STUDIO_EDIT]: false,
    [FEATURE_PERMISSIONS.CONTENT_STUDIO_DELETE]: false,
    [FEATURE_PERMISSIONS.CONTENT_STUDIO_TEMPLATES]: false,
    // Scheduler - view only
    [FEATURE_PERMISSIONS.SCHEDULER_CREATE]: false,
    [FEATURE_PERMISSIONS.SCHEDULER_EDIT]: false,
    [FEATURE_PERMISSIONS.SCHEDULER_DELETE]: false,
    [FEATURE_PERMISSIONS.SCHEDULER_STATUS]: false
  };
};

// Feature permission labels for UI
export const FEATURE_PERMISSION_LABELS = {
  [FEATURE_PERMISSIONS.MARKETING_TRACKER]: {
    label: 'Marketing Tracker',
    description: 'View and access marketing performance data'
  },
  [FEATURE_PERMISSIONS.ON_TRACK_PACING]: {
    label: 'On Track Pacing',
    description: 'View pacing metrics and goal progress'
  },
  [FEATURE_PERMISSIONS.ADS_MANAGER]: {
    label: 'Ads Manager',
    description: 'Manage advertising campaigns and budgets'
  },
  [FEATURE_PERMISSIONS.TARGET_NUMBERS]: {
    label: 'Target Numbers',
    description: 'Edit target goals and KPI settings'
  },
  [FEATURE_PERMISSIONS.MARKETING_SETTINGS]: {
    label: 'Marketing Settings',
    description: 'Configure marketing module settings'
  }
};
